import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, Clock, User as UserIcon, FileEdit, Plus, Trash2, Save, FileSpreadsheet, Camera, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPT_COLOR = {
  ART:               '#c084fc',
  COSTUME_WARDROBE:  '#facc15',
  MAKEUP_HAIR:       '#f472b6',
  CASTING:           '#60a5fa',
  VFX:               '#22d3ee',
  PRODUCTION:        '#4ade80',
  SCHEDULE:          '#fb923c',
  SOUND:             '#fb7185',
  SFX_STUNTS:        '#fb7185',
  PRODUCER:          '#e8c547',
  DIRECTOR:          '#e8c547',
  OTHER:             '#a0a0a0',
};

const ActivityLogPage = () => {
  const { projectId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userFilter, setUserFilter] = useState('ALL');
  const [collapsed, setCollapsed] = useState({});  // { [dept]: bool }

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/projects/${projectId}/activity?limit=500`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLogs(r.data || []);
      } catch (e) {
        toast.error('Failed to load activity log');
      } finally { setLoading(false); }
    })();
  }, [projectId, token]);

  const getActionIcon = (action) => {
    if (!action) return <Clock className="h-3 w-3" />;
    if (action.includes('scene')) return <FileEdit className="h-3 w-3" />;
    if (action.includes('item')) return action.includes('added') ? <Plus className="h-3 w-3" /> : action.includes('deleted') ? <Trash2 className="h-3 w-3" /> : <Save className="h-3 w-3" />;
    if (action.includes('snapshot')) return <Camera className="h-3 w-3" />;
    if (action.includes('excel') || action.includes('import')) return <FileSpreadsheet className="h-3 w-3" />;
    return <Clock className="h-3 w-3" />;
  };

  const getActionColor = (action) => {
    if (!action) return 'bg-[#e8c547]/20 text-[#e8c547]';
    if (action.includes('added') || action.includes('created')) return 'bg-green-500/20 text-green-400';
    if (action.includes('deleted')) return 'bg-red-500/20 text-red-400';
    if (action.includes('updated') || action.includes('imported')) return 'bg-blue-500/20 text-blue-400';
    return 'bg-[#e8c547]/20 text-[#e8c547]';
  };

  const formatAction = (a) => (a || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  // Distinct users for filter
  const allUsers = useMemo(() => {
    const seen = new Map();
    logs.forEach(l => {
      const id = l.user_id || l.user_name;
      if (id && !seen.has(id)) seen.set(id, l.user_name || 'Unknown');
    });
    return Array.from(seen.entries());   // [[id, name]]
  }, [logs]);

  // Filter + group by department
  const grouped = useMemo(() => {
    const filtered = userFilter === 'ALL' ? logs : logs.filter(l => (l.user_id || l.user_name) === userFilter);
    const map = {};
    filtered.forEach(l => {
      const d = (l.user_department || 'OTHER').toUpperCase();
      if (!map[d]) map[d] = [];
      map[d].push(l);
    });
    // Sort each group's items by created_at desc (logs come desc but be safe)
    Object.values(map).forEach(arr => arr.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')));
    return map;
  }, [logs, userFilter]);

  const deptKeys = Object.keys(grouped).sort();
  const totalShown = Object.values(grouped).reduce((s, arr) => s + arr.length, 0);

  return (
    <div className="min-h-screen bg-[#0d0d0d] p-4 md:p-8" data-testid="activity-log-page">
      <div className="max-w-5xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(`/projects/${projectId}/breakdown`)}
          className="text-[#a0a0a0] hover:text-[#f0f0f0] mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Breakdown
        </Button>
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="font-['DM_Serif_Display'] text-3xl md:text-4xl text-[#f0f0f0] mb-1">Activity Log</h1>
            <p className="text-[#a0a0a0] text-sm">{totalShown} activities • grouped by department</p>
          </div>
          {/* User filter */}
          <div className="flex items-center gap-2" data-testid="activity-user-filter">
            <span className="text-xs text-[#a0a0a0]">Filter by user:</span>
            <select
              value={userFilter}
              onChange={e => setUserFilter(e.target.value)}
              className="bg-[#1a1a1a] border border-[#2a2a2a] text-[#f0f0f0] text-xs rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#e8c547]"
              data-testid="activity-user-select"
            >
              <option value="ALL">All users</option>
              {allUsers.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#a0a0a0]">Loading activity...</div>
        ) : totalShown === 0 ? (
          <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-12 text-center" data-testid="activity-empty">
            <Clock className="h-16 w-16 text-[#555] mx-auto mb-4" />
            <p className="text-[#a0a0a0]">No activity yet. Edits will appear here.</p>
          </Card>
        ) : (
          <div className="space-y-6">
            {deptKeys.map(dept => {
              const items = grouped[dept];
              const isCollapsed = !!collapsed[dept];
              const tint = DEPT_COLOR[dept] || '#a0a0a0';
              return (
                <div key={dept} data-testid={`activity-dept-${dept}`}>
                  <button
                    onClick={() => setCollapsed(c => ({ ...c, [dept]: !c[dept] }))}
                    className="w-full flex items-center gap-2 mb-2 text-left"
                    data-testid={`activity-dept-toggle-${dept}`}
                  >
                    {isCollapsed ? <ChevronRight className="h-4 w-4 text-[#a0a0a0]" /> : <ChevronDown className="h-4 w-4 text-[#a0a0a0]" />}
                    <span className="text-sm font-semibold uppercase tracking-wider" style={{ color: tint }}>
                      {dept.replace(/_/g, ' ')}
                    </span>
                    <span className="text-xs text-[#666]">({items.length})</span>
                    <div className="flex-1 h-px bg-[#2a2a2a] ml-2" />
                  </button>
                  {!isCollapsed && (
                    <div className="space-y-2 ml-6" data-testid={`activity-list-${dept}`}>
                      {items.map(log => (
                        <Card key={log.id} className="bg-[#1a1a1a] border-[#2a2a2a] p-3" data-testid={`activity-${log.id}`}>
                          <div className="flex items-start gap-2">
                            <Badge className={`${getActionColor(log.action)} mt-0.5 flex-shrink-0 h-5 px-1.5`}>
                              {getActionIcon(log.action)}
                            </Badge>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap text-xs">
                                <UserIcon className="h-3 w-3 text-[#a0a0a0]" />
                                <span className="text-[#f0f0f0] font-medium">{log.user_name || 'Unknown'}</span>
                                <span className="text-[#a0a0a0]">{formatAction(log.action)}</span>
                                <Badge variant="outline" className="border-[#2a2a2a] text-[#a0a0a0] text-[10px] h-4">{log.entity_type}</Badge>
                              </div>
                              <div className="text-[10px] text-[#666] mt-0.5">{log.created_at ? new Date(log.created_at).toLocaleString() : ''}</div>
                              {log.changes && (log.changes.before || log.changes.after) && (
                                <details className="mt-1.5">
                                  <summary className="text-[10px] text-[#a0a0a0] cursor-pointer hover:text-[#f0f0f0]">Show diff</summary>
                                  <div className="mt-1 text-[10px] text-[#a0a0a0] bg-[#0d0d0d] p-2 rounded font-mono overflow-x-auto">
                                    {log.changes.before && Object.keys(log.changes.before).length > 0 && (
                                      <div>
                                        <span className="text-red-400">Before:</span>
                                        <pre className="text-[10px]">{JSON.stringify(log.changes.before, null, 2)}</pre>
                                      </div>
                                    )}
                                    {log.changes.after && Object.keys(log.changes.after).length > 0 && (
                                      <div>
                                        <span className="text-green-400">After:</span>
                                        <pre className="text-[10px]">{JSON.stringify(log.changes.after, null, 2)}</pre>
                                      </div>
                                    )}
                                  </div>
                                </details>
                              )}
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogPage;
